import { useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { FolderOpen, Upload, CheckCircle2, Loader2, Download, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pubblico`;
const err = (e) => e?.response?.data?.detail || "Errore. Riprova.";
const dIt = (s) => (s ? new Date(s).toLocaleDateString("it-IT") : "");

function Shell({ title, children }) {
  return (
    <div className="min-h-screen bg-[#F7F7F8] flex items-start justify-center p-4 pt-10">
      <div className="w-full max-w-lg bg-white rounded-2xl border border-[var(--border)] p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-1 text-[#FC82ED]"><FolderOpen size={20} /><span className="text-[11px] font-bold uppercase tracking-[0.18em]">Immobilart</span></div>
        <h1 className="font-display text-xl font-extrabold mb-4">{title}</h1>
        {children}
      </div>
    </div>
  );
}

function Codice({ onOk, testid }) {
  const [c, setC] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => { setBusy(true); setMsg(""); try { await onOk(c.trim()); } catch (e) { setMsg(err(e)); } finally { setBusy(false); } };
  return (
    <div data-testid={`${testid}-codice-box`}>
      <p className="text-sm text-[var(--muted)] mb-3 flex items-center gap-1.5"><Lock size={14} /> Inserisci il codice di 6 cifre ricevuto dall'agenzia.</p>
      <input data-testid={`${testid}-codice-input`} value={c} onChange={(e) => setC(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="000000"
             className="w-full px-3 py-3 rounded-xl border border-[var(--border)] text-center text-2xl tracking-[0.4em] mono" />
      {msg && <p data-testid={`${testid}-errore`} className="text-sm text-red-600 mt-2">{msg}</p>}
      <button data-testid={`${testid}-accedi-btn`} disabled={c.length !== 6 || busy} onClick={go} className="mt-3 w-full py-3 rounded-xl bg-[#FC82ED] text-black font-semibold disabled:opacity-50">{busy ? "Verifico…" : "Accedi"}</button>
    </div>
  );
}

export function PubRichiesta() {
  const { token } = useParams();
  const [codice, setCodice] = useState("");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState("");
  const accedi = async (c) => { const r = await axios.post(`${API}/richiesta/${token}/accedi`, { codice: c }); setCodice(c); setD(r.data); };
  const carica = async (tipo, file) => {
    if (!file) return;
    setBusy(tipo); setMsg("");
    const fd = new FormData(); fd.append("codice", codice); fd.append("doc_tipo", tipo); fd.append("file", file);
    try { await axios.post(`${API}/richiesta/${token}/carica`, fd); await accedi(codice); setMsg("Documento caricato, grazie!"); } catch (e) { setMsg(err(e)); } finally { setBusy(null); }
  };
  return (
    <Shell title="Documenti richiesti">
      {!d ? <Codice onOk={accedi} testid="pub-richiesta" /> : (
        <div data-testid="pub-richiesta-lista">
          <p className="text-sm text-[var(--muted)] mb-3">Immobile: <b className="text-[#111]">{d.immobile}</b> · link valido fino al {dIt(d.scade_il)}</p>
          <div className="space-y-2">
            {d.documenti.map((x) => (
              <label key={x.doc_tipo} data-testid={`pub-doc-${x.doc_tipo}`} className="flex items-center justify-between gap-2 p-3 rounded-xl border border-[var(--border)] cursor-pointer hover:border-[#FC82ED]">
                <span className="text-sm font-medium">{x.label}</span>
                {busy === x.doc_tipo ? <Loader2 size={18} className="animate-spin text-[#FC82ED]" /> : x.caricato
                  ? <span className="text-xs font-bold text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={15} /> Caricato</span>
                  : <span className="text-xs font-semibold inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#FC82ED] text-black"><Upload size={13} /> Carica</span>}
                <input data-testid={`pub-file-${x.doc_tipo}`} type="file" hidden onChange={(e) => carica(x.doc_tipo, e.target.files?.[0])} />
              </label>
            ))}
          </div>
          {msg && <p data-testid="pub-richiesta-msg" className="text-sm mt-3">{msg}</p>}
          <p className="text-[11px] text-[var(--muted)] mt-4">Formati: PDF, JPG, PNG (max 25 MB). Puoi anche scattare una foto dal telefono.</p>
        </div>
      )}
    </Shell>
  );
}

export function PubCondivisione() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const accedi = async (c) => { const r = await axios.post(`${API}/condivisione/${token}/accedi`, { codice: c }); setD(r.data); };
  return (
    <Shell title="Documenti condivisi (sola lettura)">
      {!d ? <Codice onOk={accedi} testid="pub-condivisione" /> : (
        <div data-testid="pub-condivisione-lista">
          <p className="text-sm text-[var(--muted)] mb-3">Pratica: <b className="text-[#111]">{d.immobile}</b> · disponibile fino al {dIt(d.scade_il)}</p>
          <div className="divide-y divide-slate-100 border border-[var(--border)] rounded-xl">
            {d.files.map((f) => (
              <a key={f.id} data-testid={`pub-share-file-${f.id}`} href={`${API}/condivisione/${token}/file/${f.id}?k=${d.chiave}`} target="_blank" rel="noreferrer"
                 className="flex items-center justify-between gap-2 p-3 text-sm hover:bg-slate-50"><span className="truncate">{f.nome}</span><Download size={15} className="shrink-0 text-[#FC82ED]" /></a>
            ))}
          </div>
        </div>
      )}
    </Shell>
  );
}
